const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/Knopf/Knopf.tsx';
import { Button, Icon, useMediaQuery } from '@mui/material';
import React, { ReactElement, ReactNode, useMemo } from 'react';
import { css, cx } from '@emotion/css';
import { useTheme } from '@mui/material/styles';
import { TextInput, useOptionalText } from '../../utils/intlUtils';
import { useSelector } from 'react-redux';
import { selectDeviceOrientation } from '../../selectors/deviceSelectors';
import { ActionType } from '@cistec/common_web/components/defs';

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            knopf: css`
                min-width: 44px;
                padding: 6px 16px;
                gap: 6px;
                cursor: pointer;

                &.hasIcon:not(.hasText) {
                    padding-left: 8px;
                    padding-right: 8px;
                }

                & * {
                    cursor: inherit;
                }

                &.text,
                &.text * {
                    color: ${theme.palette.primary.main};
                }

                &.text {
                    padding-left: 4px;
                    padding-right: 4px;
                }

                &.text.neutral,
                &.text.neutral * {
                    color: rgba(0, 0, 0, 0.54);
                }

                &.text.destructive,
                &.text.destructive * {
                    color: ${theme.palette.error.main};
                }

                &.contained,
                &.contained * {
                    color: ${theme.palette.primary.contrastText};
                }

                &.contained.destructive {
                    background-color: ${theme.palette.error.main};
                }

                &.contained.destructive,
                &.contained.destructive * {
                    color: #ffffff;
                }

                &.disabled,
                &.disabled * {
                    cursor: default;
                    color: ${theme.palette.action.disabled};
                }
            `,
        }),
        [theme]
    );
}

export type KnopfType = 'contained' | 'text' | 'outlined';

interface Props {
    actionType?: ActionType;
    children?: ReactNode;
    className?: string;
    disabled?: boolean;
    do: () => void;
    hideTextWhenNarrow?: boolean; // When having an icon, show only icon when available space is narrow
    isNarrow?: boolean; // undefined=use a fixed breakpoint (but always false in landscape)
    icon?: string | ReactElement<any>;
    text?: TextInput;
    type?: KnopfType;
}

export const Knopf = (props: Props) => {
    const { actionType = 'main', type = 'contained', disabled = false, hideTextWhenNarrow = false } = props;
    const theme = useTheme();
    const orientation = useSelector(selectDeviceOrientation);
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const isNarrow = props.isNarrow ?? (isSM && orientation === 'PORTRAIT');
    const classes = useCSSClasses();

    const optionalText = useOptionalText(props.text);
    const content = props.children || optionalText;

    const renderIcon = () => {
        const icon = props.icon;
        if (typeof icon === 'string') {
            return <Icon className={icon} color="inherit" />;
        } else if (icon) {
            return icon;
        }
    };

    const icon = renderIcon();
    const hasIcon = !!icon;

    return (
        <Button
            className={cx('Knopf', classes.knopf, props.className, type, {
                hasIcon,
                hasText: !!content,
                disabled,
                [actionType]: !disabled,
            })}
            variant={type}
            onClick={props.do}
            disabled={disabled}
        >
            {icon}
            {hideTextWhenNarrow && isNarrow && hasIcon ? '' : content}
        </Button>
    );
};
